import React from 'react';
import { FileText, Calendar, Code, Trash2, Edit } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Heading, Text, Caption } from '../ui/Typography';
import { cn } from '../../utils/cn';

interface Project {
  id: string;
  name: string;
  description?: string;
  repository_url?: string;
  created_at: string;
  updated_at: string;
  blueprint_status?: 'draft' | 'locked' | 'executing' | 'complete';
  last_build_date?: string;
  compliance_score?: number;
}

interface ProjectCardProps {
  project: Project;
  isSelected?: boolean;
  onSelect: (projectId: string) => void;
  onDelete: (projectId: string) => void;
  onViewDetails?: (projectId: string) => void;
}

export function ProjectCard({ project, isSelected, onSelect, onDelete, onViewDetails }: ProjectCardProps) {
  const handleClick = () => {
    onSelect(project.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(project.id);
  };

  const statusVariantMap = {
    complete: 'success' as const,
    executing: 'default' as const,
    locked: 'warning' as const,
    draft: 'secondary' as const,
  };

  return (
    <Card
      onClick={handleClick}
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2',
        isSelected ? 'border-2 border-primary shadow-md' : 'border border-border shadow-sm'
      )}
    >
      <CardContent>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/20 rounded-md"
            >
              <Code className="w-5 h-5 text-primary" />
            </div>
            <div>
              <Heading level={4}>
                {project.name}
              </Heading>
              {project.description && (
                <Text size="sm" className="mt-1 line-clamp-2 text-text-secondary">
                  {project.description}
                </Text>
              )}
            </div>
          </div>
          <button
            onClick={handleDelete}
            className="p-1 transition-colors text-text-tertiary hover:text-destructive"
            aria-label="Delete project"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 text-text-tertiary" />
            <Caption>{new Date(project.created_at).toLocaleDateString()}</Caption>
          </div>
          {project.blueprint_status && (
            <Badge variant={statusVariantMap[project.blueprint_status] || 'secondary'}>
              {project.blueprint_status}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

